import { useState, useCallback } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, GripVertical } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { SettingsSaveBar } from '~/pages/SchoolSettings/SettingsPanel';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { useSchoolYearTermsQuery } from './useSchoolYearTermsQuery';
import { useCreateTerm } from './useCreateTerm';
import { useUpdateTerm } from './useUpdateTerm';
import { useDeleteTerm } from './useDeleteTerm';
import { useReorderTerms } from './useReorderTerms';

interface Term {
  id: string;
  name: string;
  slug: string;
  position: number | null;
  startsOn: string | null;
  endsOn: string | null;
}

interface EditedTerm {
  name?: string;
  startsOn?: string;
  endsOn?: string;
}

const PRESETS = [
  {
    label: 'Quarters',
    terms: [
      { name: 'Q1' },
      { name: 'Q2' },
      { name: 'Q3' },
      { name: 'Q4' },
    ],
  },
  {
    label: 'Semesters',
    terms: [
      { name: 'Semester 1' },
      { name: 'Semester 2' },
    ],
  },
  {
    label: 'Trimesters',
    terms: [
      { name: 'Trimester 1' },
      { name: 'Trimester 2' },
      { name: 'Trimester 3' },
    ],
  },
];

interface TermsSectionProps {
  schoolYearId: string;
}

export default function TermsSection({ schoolYearId }: TermsSectionProps) {
  const { data, loading, refetch } = useSchoolYearTermsQuery(schoolYearId);
  const [createTerm] = useCreateTerm();
  const [updateTerm] = useUpdateTerm();
  const [deleteTerm] = useDeleteTerm();
  const [reorderTerms] = useReorderTerms();

  const [editedTerms, setEditedTerms] = useState<Record<string, EditedTerm>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<Term | null>(null);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);

  const terms: Term[] = (data?.schoolYear?.terms ?? []).map((t: any) => ({
    id: t.id,
    name: t.name,
    slug: t.slug,
    position: t.position,
    startsOn: t.startsOn,
    endsOn: t.endsOn,
  }));

  const hasChanges = Object.keys(editedTerms).length > 0;

  const handleFieldChange = useCallback((termId: string, field: keyof EditedTerm, value: string) => {
    setEditedTerms((prev) => ({
      ...prev,
      [termId]: { ...prev[termId], [field]: value },
    }));
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      for (const [termId, edits] of Object.entries(editedTerms)) {
        await updateTerm({
          variables: {
            input: {
              id: termId,
              ...(edits.name !== undefined && { name: edits.name }),
              ...(edits.startsOn !== undefined && { startsOn: edits.startsOn || null }),
              ...(edits.endsOn !== undefined && { endsOn: edits.endsOn || null }),
            },
          },
        });
      }
      setEditedTerms({});
      await refetch();
    } finally {
      setSaving(false);
    }
  }, [editedTerms, updateTerm, refetch]);

  const handleCancel = useCallback(() => {
    setEditedTerms({});
  }, []);

  const handleAddTerm = useCallback(async () => {
    setAdding(true);
    try {
      const position = terms.length + 1;
      await createTerm({
        variables: {
          input: {
            schoolYearId,
            name: `Term ${position}`,
            position,
          },
        },
      });
      await refetch();
    } finally {
      setAdding(false);
    }
  }, [createTerm, schoolYearId, terms.length, refetch]);

  const handleDelete = useCallback(async () => {
    if (!deleteConfirm) return;
    await deleteTerm({ variables: { input: { id: deleteConfirm.id } } });
    setDeleteConfirm(null);
    // Clear any edits for this term
    setEditedTerms((prev) => {
      const next = { ...prev };
      delete next[deleteConfirm.id];
      return next;
    });
    await refetch();
  }, [deleteConfirm, deleteTerm, refetch]);

  const handleMove = useCallback(
    async (index: number, direction: 'up' | 'down') => {
      const newTerms = [...terms];
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      if (swapIndex < 0 || swapIndex >= newTerms.length) return;

      [newTerms[index], newTerms[swapIndex]] = [newTerms[swapIndex], newTerms[index]];
      const termIds = newTerms.map((t) => t.id);

      await reorderTerms({
        variables: { input: { schoolYearId, termIds } },
      });
      await refetch();
    },
    [terms, reorderTerms, schoolYearId, refetch]
  );

  const handlePreset = useCallback(
    async (preset: (typeof PRESETS)[0]) => {
      // Only apply if no terms exist
      if (terms.length > 0) return;
      setAdding(true);
      try {
        for (let i = 0; i < preset.terms.length; i++) {
          await createTerm({
            variables: {
              input: {
                schoolYearId,
                name: preset.terms[i].name,
                position: i + 1,
              },
            },
          });
        }
        await refetch();
      } finally {
        setAdding(false);
      }
    },
    [terms.length, createTerm, schoolYearId, refetch]
  );

  if (loading) {
    return <p className="py-6 text-sm text-muted-foreground">Loading terms…</p>;
  }

  return (
    <>
      <div id="academic-terms" className="space-y-3 scroll-mt-6">
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={handleAddTerm} disabled={adding}>
            <Plus className="h-4 w-4 mr-1" />
            Add term
          </Button>
        </div>
          {/* Quick setup presets (only when empty) */}
          {terms.length === 0 && (
            <div className="rounded-lg border border-dashed p-4 text-center space-y-3">
              <p className="text-sm text-muted-foreground">
                No terms configured. Start with a preset or add terms manually.
              </p>
              <div className="flex justify-center gap-2">
                {PRESETS.map((preset) => (
                  <Button
                    key={preset.label}
                    variant="outline"
                    size="sm"
                    onClick={() => handlePreset(preset)}
                    disabled={adding}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Term list */}
          {terms.map((term, index) => {
            const edits = editedTerms[term.id] ?? {};
            return (
              <div
                key={term.id}
                className="flex items-center gap-3 rounded-lg border p-3 bg-card hover:bg-muted/30 transition-colors"
              >
                {/* Drag handle / position indicator */}
                <div className="flex flex-col gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    onClick={() => handleMove(index, 'up')}
                    disabled={index === 0}
                  >
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                  <GripVertical className="h-4 w-4 text-muted-foreground mx-auto" />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    onClick={() => handleMove(index, 'down')}
                    disabled={index === terms.length - 1}
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </div>

                {/* Name */}
                <div className="flex-1 min-w-0">
                  <Input
                    value={edits.name ?? term.name}
                    onChange={(e) => handleFieldChange(term.id, 'name', e.target.value)}
                    className="font-medium"
                    placeholder="Term name"
                  />
                </div>

                {/* Date range */}
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={edits.startsOn ?? term.startsOn ?? ''}
                    onChange={(e) => handleFieldChange(term.id, 'startsOn', e.target.value)}
                    className="w-36"
                  />
                  <span className="text-muted-foreground text-sm">to</span>
                  <Input
                    type="date"
                    value={edits.endsOn ?? term.endsOn ?? ''}
                    onChange={(e) => handleFieldChange(term.id, 'endsOn', e.target.value)}
                    className="w-36"
                  />
                </div>

                {/* Delete */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => setDeleteConfirm(term)}
                  aria-label={`Delete ${term.name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
      </div>

      <SettingsSaveBar
        dirty={hasChanges}
        saving={saving}
        onSave={handleSave}
        onDiscard={handleCancel}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Term</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{deleteConfirm?.name}&rdquo;? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
